export type ScheduledPublishRow = {
  id: string;
  platform: string;
  status: string;
  scheduled_at?: string | null;
  clip_id?: string | null;
  clip_name?: string | null;
  title?: string | null;
  caption?: string | null;
  social_connection_id?: string | null;
  publish_queue?: { id: string; name: string } | null;
};

export type QueuePostSource =
  | { kind: 'queue' }
  | { kind: 'otherQueue'; queueName: string }
  | { kind: 'manual' };

export type QueueScheduledPost = {
  key: string;
  clipId: string | null;
  label: string | null;
  scheduledAt: string;
  platforms: string[];
  source: QueuePostSource;
};

type QueueScope = {
  id: string;
  spacing_scope?: string | null;
  social_connection_ids: string[];
};

function firstText(...values: Array<string | null | undefined>): string | null {
  const found = values.find((value) => typeof value === 'string' && value.trim());
  return found ? found.trim() : null;
}

function sourceOf(row: ScheduledPublishRow, queueId: string): QueuePostSource {
  if (!row.publish_queue) return { kind: 'manual' };
  if (row.publish_queue.id === queueId) return { kind: 'queue' };
  return { kind: 'otherQueue', queueName: row.publish_queue.name };
}

/**
 * Upcoming posts to show under a queue on /queues, mirroring what the backend
 * allocator treats as occupied: the queue's own rows, plus (in `account`
 * spacing) every other scheduled row on the queue's accounts. Rows of one
 * cross-post (same clip, same instant) collapse into one entry.
 */
export function selectQueueScheduledPosts({
  queue,
  publishes,
  now,
}: {
  queue: QueueScope;
  publishes: ScheduledPublishRow[];
  now: Date;
}): QueueScheduledPost[] {
  const accountSpaced = queue.spacing_scope === 'account';
  const accountIds = new Set(queue.social_connection_ids);
  const posts = new Map<string, QueueScheduledPost>();

  publishes.forEach((row) => {
    if (row.status !== 'scheduled' || !row.scheduled_at) return;
    const at = new Date(row.scheduled_at);
    if (Number.isNaN(at.getTime()) || at.getTime() <= now.getTime()) return;
    const ownRow = row.publish_queue?.id === queue.id;
    const onQueueAccount = Boolean(row.social_connection_id && accountIds.has(row.social_connection_id));
    if (!ownRow && !(accountSpaced && onQueueAccount)) return;

    const scheduledAt = at.toISOString();
    const key = `${row.clip_id ?? row.id}|${scheduledAt}`;
    const existing = posts.get(key);
    if (existing) {
      if (!existing.platforms.includes(row.platform)) existing.platforms.push(row.platform);
      return;
    }
    posts.set(key, {
      key,
      clipId: row.clip_id ?? null,
      label: firstText(row.clip_name, row.title, row.caption),
      scheduledAt,
      platforms: [row.platform],
      source: sourceOf(row, queue.id),
    });
  });

  return [...posts.values()].sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
}
